/**
 * InfrastructureCostCard - GCP infrastructure cost monitoring (Task 081)
 *
 * Displays:
 * - Total infrastructure cost for the period
 * - Cost breakdown by GCP service
 * - Combined platform cost (API + infra)
 * - True per-request cost including infrastructure overhead
 */

import React from 'react';
import { Card, CardSkeleton, Button } from '@/components/ui';
import type {
  InfrastructureCostSummary,
  InfrastructureCostTrendsResponse,
  CombinedCostSummary,
  CostPeriod,
} from '@/services/admin/AdminService';

interface InfrastructureCostCardProps {
  infraSummary: InfrastructureCostSummary | null;
  infraTrends: InfrastructureCostTrendsResponse | null;
  combinedSummary: CombinedCostSummary | null;
  loading: boolean;
  period: CostPeriod;
  onPeriodChange: (period: CostPeriod) => void;
  onRefresh: () => void;
}

// Format USD with proper precision
const formatCost = (value: number): string => {
  if (value === 0) return '$0.00';
  if (value < 0.01) return `$${value.toFixed(4)}`;
  if (value < 1) return `$${value.toFixed(3)}`;
  return `$${value.toFixed(2)}`;
};

// Server/infrastructure icon
const ServerIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className || 'w-5 h-5'}
  >
    <path d="M4.08 5.227A3 3 0 016.979 3H17.02a3 3 0 012.9 2.227l2.113 7.926A5.228 5.228 0 0018.75 12H5.25a5.228 5.228 0 00-3.284 1.153L4.08 5.227z" />
    <path
      fillRule="evenodd"
      d="M5.25 13.5a3.75 3.75 0 100 7.5h13.5a3.75 3.75 0 100-7.5H5.25zm10.5 4.5a.75.75 0 100-1.5.75.75 0 000 1.5zm3.75-.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"
      clipRule="evenodd"
    />
  </svg>
);

// Period selector button group
const PeriodSelector: React.FC<{
  period: CostPeriod;
  onPeriodChange: (period: CostPeriod) => void;
}> = ({ period, onPeriodChange }) => (
  <div className="inline-flex rounded-lg border border-secondary-200 bg-white p-1">
    {(['day', 'week', 'month'] as CostPeriod[]).map((p) => (
      <button
        key={p}
        onClick={() => onPeriodChange(p)}
        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
          period === p
            ? 'bg-primary-100 text-primary-700'
            : 'text-secondary-600 hover:bg-secondary-50'
        }`}
      >
        {p.charAt(0).toUpperCase() + p.slice(1)}
      </button>
    ))}
  </div>
);

// Service cost breakdown with colored bars
const ServiceBreakdown: React.FC<{ costByService: Record<string, number> }> = ({
  costByService,
}) => {
  const sortedServices = Object.entries(costByService)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6);

  const total = Object.values(costByService).reduce((sum, v) => sum + v, 0);

  // Color mapping for common GCP services
  const serviceColors: Record<string, string> = {
    'Cloud Run': 'bg-blue-500',
    'Cloud Firestore': 'bg-orange-500',
    'Cloud Storage': 'bg-green-500',
    'Vertex AI': 'bg-purple-500',
    'Cloud Logging': 'bg-yellow-500',
    'Cloud Monitoring': 'bg-pink-500',
  };

  return (
    <div className="space-y-2">
      {sortedServices.map(([service, cost]) => {
        const percentage = total > 0 ? (cost / total) * 100 : 0;
        const colorClass = serviceColors[service] || 'bg-secondary-500';
        return (
          <div key={service} className="flex items-center">
            <span className="w-28 text-sm text-secondary-600 truncate">
              {service}
            </span>
            <div className="flex-1 mx-3">
              <div className="h-2 bg-secondary-100 rounded-full overflow-hidden">
                <div
                  className={`h-full ${colorClass} rounded-full`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
            <span className="w-16 text-sm text-secondary-700 text-right">
              {formatCost(cost)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const InfrastructureCostCard: React.FC<InfrastructureCostCardProps> = ({
  infraSummary,
  infraTrends,
  combinedSummary,
  loading,
  period,
  onPeriodChange,
  onRefresh,
}) => {
  if (loading) {
    return <CardSkeleton />;
  }

  return (
    <Card padding="lg">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <ServerIcon className="w-6 h-6 mr-2 text-blue-600" />
          <h3 className="text-lg font-semibold text-secondary-900">
            Infrastructure Costs
          </h3>
        </div>
        <div className="flex items-center space-x-3">
          <PeriodSelector period={period} onPeriodChange={onPeriodChange} />
          <Button variant="ghost" size="sm" onClick={onRefresh}>
            Refresh
          </Button>
        </div>
      </div>

      {!infraSummary ? (
        <div className="text-center py-8">
          <p className="text-secondary-500">
            Infrastructure cost data not available. Ensure BigQuery billing
            export is configured.
          </p>
        </div>
      ) : (
        <>
          {/* Billing delay notice */}
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-700">
              Note: GCP billing data has ~24 hour delay. Latest data from{' '}
              {new Date(
                infraSummary.metrics.data_freshness
              ).toLocaleDateString()}
            </p>
          </div>

          {/* Combined metrics highlight */}
          {combinedSummary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-3xl font-bold text-blue-600">
                  {formatCost(combinedSummary.metrics.total_platform_cost_usd)}
                </p>
                <p className="text-sm font-medium text-secondary-700">
                  Total Platform Cost
                </p>
              </div>
              <div className="text-center p-4">
                <p className="text-2xl font-bold text-secondary-900">
                  {formatCost(combinedSummary.metrics.api_cost_usd)}
                </p>
                <p className="text-sm text-secondary-600">API Costs</p>
              </div>
              <div className="text-center p-4">
                <p className="text-2xl font-bold text-secondary-900">
                  {formatCost(combinedSummary.metrics.infrastructure_cost_usd)}
                </p>
                <p className="text-sm text-secondary-600">Infrastructure</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">
                  {formatCost(combinedSummary.metrics.true_per_request_cost)}
                </p>
                <p className="text-sm text-secondary-600">True Cost/Request</p>
              </div>
            </div>
          )}

          {/* Per-request breakdown */}
          {combinedSummary && (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="border border-secondary-200 rounded-lg p-4">
                <p className="text-sm text-secondary-600 mb-1">
                  API Cost per Request
                </p>
                <p className="text-xl font-semibold text-secondary-900">
                  {formatCost(combinedSummary.metrics.per_request_api_cost)}
                </p>
              </div>
              <div className="border border-secondary-200 rounded-lg p-4">
                <p className="text-sm text-secondary-600 mb-1">
                  Infra Overhead per Request
                </p>
                <p className="text-xl font-semibold text-secondary-900">
                  {formatCost(combinedSummary.metrics.per_request_infra_cost)}
                </p>
              </div>
            </div>
          )}

          {/* Service breakdown */}
          <div className="border border-secondary-200 rounded-lg p-4 mb-6">
            <h4 className="text-sm font-medium text-secondary-700 mb-3">
              Cost by GCP Service
            </h4>
            {Object.keys(infraSummary.metrics.cost_by_service).length > 0 ? (
              <ServiceBreakdown
                costByService={infraSummary.metrics.cost_by_service}
              />
            ) : (
              <p className="text-sm text-secondary-500">
                No service data available
              </p>
            )}
          </div>

          {/* Projected cost */}
          {infraTrends?.projected_monthly_cost && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                <span className="font-medium">
                  Projected Monthly Infrastructure:
                </span>{' '}
                {formatCost(infraTrends.projected_monthly_cost)}
                <span className="text-blue-600 ml-2">
                  (based on {formatCost(infraTrends.avg_daily_cost)}/day avg)
                </span>
              </p>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default InfrastructureCostCard;
